/**
 * EVENTSFLO-PROFILE-PHOTO-SIGNED-URL-V1
 *
 * Tiny helper for minting + caching short-lived signed profile-photo
 * URLs. Used everywhere an `<img>` previously rendered
 * `/api/staff/{id}/photo` directly.
 *
 * The signed URL is served by the backend route
 *   `GET /api/staff/{id}/photo/signed?token=...`
 * which validates the HS256 token + TTL + purpose claim + staff_id
 * binding. The unauthenticated public route is now gated behind admin
 * auth (R-1 closed) — so this helper is the path forward for any
 * `<img src>` consumer.
 */
import { useEffect, useState } from 'react';
import api from './api';

// In-memory cache: { staffId -> { url, expiresAt(Date), inFlight(Promise) } }
const cache = new Map();

// Refresh a little before actual expiry to avoid races.
const REFRESH_MARGIN_MS = 30_000;

/**
 * Returns a fully-qualified signed photo URL ready for `<img src>`,
 * or `null` if the request fails (caller should show placeholder).
 *
 * Idempotent + de-duplicates concurrent calls for the same staffId.
 */
export async function getSignedPhotoUrl(staffId) {
  if (!staffId) return null;

  const now = Date.now();
  const entry = cache.get(staffId);
  if (entry && entry.url && entry.expiresAt - now > REFRESH_MARGIN_MS) {
    return entry.url;
  }
  if (entry && entry.inFlight) {
    return entry.inFlight;
  }

  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const inFlight = (async () => {
    try {
      const r = await api.post(`/staff/${staffId}/photo/signed-url`);
      const data = r.data || {};
      if (!data.url) return null;
      const expiresAt = data.expires_at
        ? new Date(data.expires_at).getTime()
        : now + 5 * 60 * 1000;
      const fullUrl = `${backendBase}${data.url}`;
      cache.set(staffId, { url: fullUrl, expiresAt, inFlight: null });
      return fullUrl;
    } catch (e) {
      cache.delete(staffId);
      return null;
    }
  })();

  cache.set(staffId, { ...(entry || {}), inFlight });
  return inFlight;
}

/**
 * Invalidate the cache entry — call after an upload / crop / delete
 * so the next render mints a fresh URL.
 */
export function invalidateSignedPhotoUrl(staffId) {
  if (!staffId) return;
  cache.delete(staffId);
}

/**
 * React hook that mints + auto-refreshes a signed profile-photo URL.
 *
 * Usage:
 *   const photoUrl = useSignedPhotoUrl(staff.id);
 *   {photoUrl && <img src={photoUrl} ... />}
 *
 * - Returns `null` until the first mint completes, or on failure.
 * - Re-mints on `staffId` change or on `cacheBuster` change (used to
 *   force a refresh after upload/crop/delete).
 */
export function useSignedPhotoUrl(staffId, cacheBuster = 0) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!staffId) {
      setUrl(null);
      return () => { alive = false; };
    }
    if (cacheBuster) {
      invalidateSignedPhotoUrl(staffId);
    }
    getSignedPhotoUrl(staffId).then((u) => {
      if (alive) setUrl(u);
    });
    return () => { alive = false; };
  }, [staffId, cacheBuster]);
  return url;
}
